import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FiDownload, FiChevronDown, FiClipboard, FiFileText, FiCode, FiPrinter, FiHash, FiLayers, FiFile,
} from 'react-icons/fi';
import Button from './Button';
import HoverPopover from './HoverPopover';

// Standard export actions map to crisp line icons by their stable `key`, so the
// call sites stay terse (no per-item icon imports) and every menu looks identical.
// A call site may still pass an explicit React-node `icon` to override; a legacy
// string/emoji icon is honoured only when the key isn't one of these.
const KEY_ICONS = {
  copy: FiClipboard,   // copy to clipboard
  txt: FiFileText,     // plain-text document
  html: FiCode,        // markup
  print: FiPrinter,    // print / browser PDF
  pdf: FiHash,         // numbered PDF — continuous page numbers
  // Word formats share a glyph that is DISTINCT from txt's: both previously
  // mapped to FiFileText, so ".TXT" and ".DOCX" rendered identically in a menu
  // offering both. FiFile (plain document) vs FiFileText (lined text) separates
  // "opens in Word" from "raw text" at a glance.
  doc: FiFile,         // HTML-in-Word download
  docx: FiFile,        // numbered DOCX — continuous page numbers
  all: FiLayers,       // every annexure combined
};

function resolveItemIcon(item) {
  if (item.icon && typeof item.icon !== 'string') return item.icon; // explicit node wins
  const Ico = KEY_ICONS[item.key];
  if (Ico) return <Ico size={15} aria-hidden="true" />;
  return item.icon || null; // legacy emoji string, or nothing
}

/**
 * ExportMenu — the single "Export ▾" dropdown used across every document surface
 * (Minutes, document/resolution draft modals, admin order detail, report
 * generator, report templates). It replaces the old row of separate
 * Copy/.TXT/.HTML/Print buttons with one consolidated overflow menu.
 *
 * Each surface passes the actions it wants via `items`; clicking an enabled item
 * runs its `onClick` and closes the menu. Open/close on outside-click + Escape
 * mirrors the pattern proven in NotificationDropdown.
 *
 * Positioning: the panel is `position: fixed`, anchored to the trigger's bounding
 * rect, and rendered through a portal into `document.body`. The portal matters:
 * a `fixed` element is re-anchored to the nearest *transformed* ancestor (CSS
 * containing-block rule), so a card with a `hover:-translate-y` lift would drag
 * the menu far off-screen. Portalling to <body> keeps it viewport-anchored no
 * matter what transforms sit between the trigger and the root. It also clears any
 * ancestor `overflow:hidden`. Auto-flips above the trigger when room is tight.
 *
 * @param {Object}   props
 * @param {Array}    props.items     — menu items. Each: { key, label, onClick,
 *                                      icon?, disabled?, hint?, dividerBefore?, tone? }
 * @param {string}   [props.label]   — trigger text (default 'Export')
 * @param {'sm'|'md'}[props.size]    — 'sm' for modal toolbars, 'md' for page bars
 * @param {'primary'|'outline'} [props.variant] — trigger look (default 'primary' teal)
 * @param {'right'|'left'} [props.align]      — panel edge aligned to trigger (default 'right')
 * @param {'auto'|'down'|'up'} [props.direction] — open direction (default 'auto')
 * @param {boolean}  [props.disabled] — disable the whole trigger
 * @param {string}   [props.title]    — trigger tooltip
 */
export default function ExportMenu({
  items = [],
  label = 'Export',
  size = 'md',
  variant = 'primary',
  align = 'right',
  direction = 'auto',
  disabled = false,
  title,
  iconOnly = false,   // render a bare download glyph (matches the icon-only action rows)
  tone,               // icon colour when iconOnly (e.g. teal for export)
  className = '',     // forwarded to the trigger Button (e.g. 'cs-btn--tint')
  triggerIcon = null, // override the trigger glyph (default: download) — e.g. a share icon when the menu owns share + export
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const usable = items.filter(Boolean);

  // Anchor the fixed panel to the trigger's current viewport rect.
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const estH = Math.min(usable.length, 8) * 42 + 14; // rough panel height
    const flipUp =
      direction === 'up' ||
      (direction === 'auto' && r.bottom + gap + estH > window.innerHeight - 8 && r.top > estH);
    setPos({
      top: flipUp ? undefined : Math.round(r.bottom + gap),
      bottom: flipUp ? Math.round(window.innerHeight - r.top + gap) : undefined,
      left: align === 'left' ? Math.round(r.left) : undefined,
      right: align === 'right' ? Math.round(window.innerWidth - r.right) : undefined,
    });
  }, [align, direction, usable.length]);

  // Reposition while open (follows scroll/resize, incl. scroll inside modals).
  useLayoutEffect(() => {
    if (!open) return undefined;
    computePos();
    const onScroll = () => computePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, computePos]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // The panel is portaled outside wrapRef, so check it explicitly — else a
      // mousedown on a menu item would close before its click lands.
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Positioning is owned by the layout effect below; it resolves before paint,
  // so the panel never flashes at a stale position.
  const toggle = () => setOpen((s) => !s);

  const runItem = useCallback((item) => {
    if (item.disabled) return;
    setOpen(false);
    item.onClick?.();
  }, []);

  const btnVariant = ['outline', 'ghost', 'soft'].includes(variant) ? variant : 'primary';

  return (
    <div className="relative" ref={wrapRef}>
      {iconOnly ? (
        <HoverPopover variant="tooltip" content={title || label} disabled={open}>
          <Button
            ref={triggerRef}
            onClick={toggle}
            disabled={disabled}
            aria-label={title || label}
            variant={btnVariant}
            size={size === 'sm' ? 'sm' : 'md'}
            iconOnly
            className={className}
            aria-haspopup="menu"
            aria-expanded={open}
            style={tone ? { color: tone } : undefined}
          >
            {triggerIcon || <FiDownload size={size === 'sm' ? 16 : 17} />}
          </Button>
        </HoverPopover>
      ) : (
        <Button
          ref={triggerRef}
          onClick={toggle}
          disabled={disabled}
          title={title}
          variant={btnVariant}
          size={size === 'sm' ? 'sm' : 'md'}
          className={className}
          aria-haspopup="menu"
          aria-expanded={open}
          leftIcon={triggerIcon || <FiDownload size={size === 'sm' ? 14 : 15} />}
          rightIcon={
            <FiChevronDown
              size={14}
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease', opacity: 0.85 }}
            />
          }
        >
          {label}
        </Button>
      )}

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="menu"
          className="fixed min-w-[228px] p-1.5"
          style={{
            ...pos,
            background: '#ffffff',
            border: '1px solid #e7e0d0',
            borderRadius: '14px',
            boxShadow: '0 14px 44px rgba(8,17,31,0.16), 0 2px 8px rgba(8,17,31,0.06)',
            zIndex: 1100,
          }}
        >
          {usable.map((item) => {
            const icon = resolveItemIcon(item);
            return (
              <div key={item.key}>
                {item.dividerBefore && (
                  <div className="my-1 mx-2" style={{ borderTop: '1px solid #efe8d9' }} />
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runItem(item)}
                  disabled={item.disabled}
                  className="group w-full flex items-center gap-3 rounded-[10px] px-2 py-[7px] text-left transition-colors hover:bg-[#f6f3ea] disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  style={{ color: item.tone || '#08111f' }}
                >
                  {icon != null ? (
                    <span
                      className="shrink-0 grid place-items-center w-7 h-7 rounded-[8px] bg-[#f3efe5] text-[#6b6354] text-[13px] transition-colors group-hover:bg-[#e2eeee] group-hover:text-[#145f5f]"
                      aria-hidden="true"
                    >
                      {icon}
                    </span>
                  ) : (
                    <span className="w-7 h-7 shrink-0" aria-hidden="true" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] font-semibold leading-tight">{item.label}</span>
                    {item.hint && (
                      <span className="block text-[10.5px] mt-[3px] leading-tight" style={{ color: '#a39a88' }}>
                        {item.hint}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
